"use client";

import React, { useState, useCallback } from "react";
import Image from "next/image";

const ImageLightboxGrid = ({ items = [], title }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(null);

    const openModal = useCallback((index) => {
        setActiveIndex(index);
        setIsOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setIsOpen(false);
        setActiveIndex(null);
    }, []);

    const showPrev = useCallback(() => {
        if (activeIndex === null) return;
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : items.length - 1));
    }, [activeIndex, items.length]);

    const showNext = useCallback(() => {
        if (activeIndex === null) return;
        setActiveIndex((prev) => (prev < items.length - 1 ? prev + 1 : 0));
    }, [activeIndex, items.length]);

    if (!items.length) return null;

    return (
        <section className="w-full">
            {title && (
                <h2 className="mb-4 text-xl md:text-2xl font-semibold text-gray-900">
                    {title}
                </h2>
            )}

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
                {items.map((item, idx) => (
                    <button
                        key={idx}
                        type="button"
                        onClick={() => openModal(idx)}
                        className="relative overflow-hidden aspect-[4/3] focus:outline-none"
                    >
                        <Image
                            src={item.src}
                            alt={item.alt || `Image ${idx + 1}`}
                            className="h-full w-full object-cover cursor-pointer hover:scale-105 transition-transform duration-500"
                            priority={idx === 0}
                        />
                    </button>
                ))}
            </div>

            {/* Modal */}
            {isOpen && activeIndex !== null && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
                    onClick={closeModal}
                >
                    <div
                        className="relative max-w-7xl w-[90%] sm:w-[80%] rounded-xl overflow-hidden bg-black"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Close */}
                        <button
                            onClick={closeModal}
                            className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-white/90 text-gray-900 flex items-center justify-center text-sm font-bold shadow"
                        >
                            ✕
                        </button>

                        {/* Image */}
                        <div className="relative w-full aspect-[16/9] sm:aspect-[4/3]">
                            <Image
                                src={items[activeIndex].src}
                                alt={items[activeIndex].alt || `Image ${activeIndex + 1}`}
                                className="h-full w-full object-cover"
                                fill
                            />
                        </div>

                        {/* Prev / Next */}
                        <button
                            onClick={showPrev}
                            className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/90 text-gray-900 flex items-center justify-center text-xl shadow-lg hover:bg-white"
                        >
                            ‹
                        </button>
                        <button
                            onClick={showNext}
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-white/90 text-gray-900 flex items-center justify-center text-xl shadow-lg hover:bg-white"
                        >
                            ›
                        </button>

                        {/* Counter */}
                        <div className="px-4 py-3 text-center text-sm text-gray-100 bg-black/70">
                            Image {activeIndex + 1} of {items.length}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ImageLightboxGrid;
